import React, { useEffect, useMemo, useState } from 'react';
import { Empty, Input, Tooltip } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useQueryWithStore } from 'react-admin';
import { InfiniteFoot, useInfinitePages } from '../../Common/DataList';
import { getTagForRole } from '../../Common/Tags';
import { getInitials } from '../../Common/utils';
import { formatDateAndTime, formatDateAndTimeFromNow } from '../../../helpers/utils';

/** the batch the endless scroll fetches, as everywhere else */
const PER_PAGE = 25;

/** long enough not to fire on every keystroke, short enough to feel typed-into */
const SEARCH_DEBOUNCE = 300;

/** One member, in the row shape the team's own Members tab uses. */
const MemberRow = ({ record }: { record: any }) => (
    <div className="cp-member">
        <span className="cp-member__who">
            <span className="cp-member__badge" aria-hidden="true">
                {getInitials(record.username || '?').slice(0, 2)}
            </span>
            <span className="cp-member__name">{record.username}</span>
        </span>

        <Tooltip placement="topLeft" title={record.email || undefined}>
            <span className={`cp-member__mail${record.email ? '' : ' cp-member__mail--empty'}`}>
                {record.email || '—'}
            </span>
        </Tooltip>

        <span className="cp-member__role">{getTagForRole(record.role)}</span>

        <Tooltip title={record.created ? formatDateAndTime(record.created) : undefined}>
            <span className="cp-member__joined">
                {record.created ? formatDateAndTimeFromNow(record.created) : '—'}
            </span>
        </Tooltip>
    </div>
);

/**
 * Who is on the counterparty's team.
 *
 * `team/memberships/<pov>/` answers with the members of every team standing in
 * that pov toward the caller's own team, and `team_id` narrows that to this
 * one. The scope is the endpoint's, not this component's: a team the caller has
 * no dealing with was never in the set, so it comes back with no rows rather
 * than with somebody else's.
 *
 * Not a `ListBase`, unlike every other list in the app: react-admin only keeps
 * a list's records once its resource is registered, and this one has no page of
 * its own to register for. `useQueryWithStore` caches by query instead, which is
 * what the dependant service items list does for the same reason.
 */
const CounterpartyMembers = ({ resource, teamId }: { resource: string; teamId: number }) => {
    const [page, setPage] = useState(1);
    // what has been typed, and what has been asked for — the second follows the
    // first once the typing stops
    const [term, setTerm] = useState('');
    const [search, setSearch] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => setSearch(term.trim()), SEARCH_DEBOUNCE);
        return () => clearTimeout(timer);
    }, [term]);

    const filter = useMemo(
        () => (search ? { team_id: teamId, username: search } : { team_id: teamId }),
        [teamId, search],
    );

    const { data, total, loading } = useQueryWithStore({
        type: 'getList',
        resource,
        payload: {
            pagination: { page, perPage: PER_PAGE },
            // oldest first: a team reads as the order it was built in
            sort: { field: 'created', order: 'ASC' },
            filter,
        },
    });

    const { items, hasMore, sentinelRef, settling } = useInfinitePages({
        queryKey: JSON.stringify([resource, teamId, search]),
        records: data || [],
        loading,
        total,
        page,
        setPage,
    });

    const empty = !loading && !settling && items.length === 0;

    return (
        <>
            <div className="cp-section__head">
                <h4 className="cp-section__title">
                    Members
                    {total ? <span className="cp-section__count">{total}</span> : null}
                </h4>
                <Input
                    className="cp-search"
                    size="small"
                    allowClear
                    value={term}
                    onChange={(e) => setTerm(e.target.value)}
                    prefix={<SearchOutlined />}
                    placeholder="Search by username"
                    aria-label="Search members"
                    data-cy="counterparty-member-search"
                />
            </div>

            <div className="cp-members" data-cy="counterparty-members">
                {/* skeletons only for the first page; later ones load underneath */}
                {(loading || settling) && items.length === 0 && (
                    Array.from({ length: 4 }).map((_, i) => <div key={i} className="cp-member cp-member--skel" />)
                )}

                {empty && (
                    <Empty
                        className="cp-empty"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={search ? 'No members match that name' : 'No members to show'}
                    />
                )}

                {items.map((record: any) => <MemberRow key={record.id} record={record} />)}

                <InfiniteFoot
                    sentinelRef={sentinelRef}
                    loading={loading}
                    loaded={items.length}
                    total={total ?? 0}
                    hasMore={hasMore}
                    noun="member"
                    dataCy="counterparty-members-foot"
                />
            </div>
        </>
    );
};

export default CounterpartyMembers;
